import { useContext, useEffect, useMemo, useState } from "react";
import * as turf from "@turf/turf";

// context
import MapContext from "../../services/context/map-context";
import { useCurrentResultContext } from "../../services/context/current-result-context";

// constants
import {
  ADJACENT_TYPE,
  CAR_PARKING_AREA_TYPE,
  CAR_ROUTE_TYPE,
  DOCKING_AREA_TYPE,
  OUTLINE_ROUTE_TYPE,
  TRUCK_ROUTE_TYPE,
} from "../../constants/global.constants";
import { ROUTES } from "../../constants/routes";

// utils
import {
  createDistanceAndVertexFeatures,
  polygonToLines,
} from "../../utils/map-polygon";
import {
  addBuildingDistanceLabels,
  addBuildingEdgesLayer,
  addBuildingAxisGridLayer,
  addBuildingsAndAreasLayer,
  addBuildingsNameLabels,
  addDockingBaysLayer,
  addLayerIfNotExists,
  addOrUpdateSource,
  addParkingSlotsLayer,
  addRoutesLayer,
  addRoutesMiddleLineLayer,
  removeBuildingDistanceLabels,
  removeBuildingEdgesLayer,
  removeBuildingAxisGridLayer,
  removeBuildingsAndAreasLayer,
  removeBuildingsNameLabels,
  removeDockingBaysLayer,
  removeParkingSlotsLayer,
  removeRoutesLayer,
  removeRoutesMiddleLineLayer,
  isMapExist,
} from "../../utils/map-layer";
import { addPolygonVerticesAsCircles } from "../../utils/map-labels";
import { getAxisGridShape } from "../../utils/grid-utils";

// services
import { luckyorangeTrackEven } from "../../services/lucky-orange/luckyorange-events";
import { useResultsContext } from "../../services/context/results-context";


export const BuildingLayer = ({
  buildings,
  selectedResult,
  selectedBuilding,
  setSelectedBuilding,
  routes,
  showGrid,
  getSelectedGridIndex,
}) => {
  const { map, mapLoaded } = useContext(MapContext);
  const { buildingsTypes, connectionTypes } = useResultsContext();

  const dockingAreaTypeData = buildingsTypes.find(
    (b) => b.node_name === DOCKING_AREA_TYPE,
  );
  const carParckingAreaTypeData = buildingsTypes.find(
    (b) => b.node_name === CAR_PARKING_AREA_TYPE,
  );

  const selectedGridShape = useMemo(() => {
    if (!selectedBuilding || !selectedResult) return null;

    const gridIndex = getSelectedGridIndex(
      selectedResult.resultId,
      selectedBuilding.buildingId,
    );

    return getAxisGridShape(selectedBuilding, gridIndex);
  }, [selectedBuilding, selectedResult, getSelectedGridIndex]);

  const selectedBuildingHeight = selectedBuilding?.kpis?.height || 0;


  useEffect(() => {
    if (!map) return;

    if (showGrid && selectedGridShape && selectedBuildingHeight) {
      removeBuildingAxisGridLayer(map);
      addBuildingAxisGridLayer(map, selectedGridShape, selectedBuildingHeight);
    } else {
      removeBuildingAxisGridLayer(map);
    }

    return () => {
      removeBuildingAxisGridLayer(map);
    };
  }, [map, showGrid, selectedGridShape, selectedBuildingHeight]);

  const buildingsCenter = [];
  const dockingBaysFeatures = [];

  // Prepare building features
  const buildingFeatures = buildings
    .filter(
      (b) =>
        b.buildingTypeId !== dockingAreaTypeData?.id &&
        b.buildingTypeId !== carParckingAreaTypeData?.id &&
        b.geodata,
    )
    .map((item) => {
      const buildingGeo = JSON.parse(item.geodata);
      const feature = buildingGeo.features[0];
      const buildingTypeData = buildingsTypes.find(
        (b) => b.id === item.buildingTypeId,
      );

      // Append building-specific props
      feature.properties = {
        ...feature.properties,
        height: item.kpis?.height || 0,
        color: buildingTypeData?.color || "#6d6c6b",
        name: item.name,
        type: buildingTypeData?.display_name || item.type,
        buildingId: item.buildingId,
        buildingTypeId: item.buildingTypeId,
      };

      // Calculate center for name label
      const centerPoint = turf.center(feature);
      buildingsCenter.push({
        ...centerPoint,
        properties: {
          ...centerPoint.properties,
          name: item.name,
        },
      });

      // get docking bays
      if (item.DockingBayData?.length > 0) {
        item.DockingBayData.forEach((db) => {
          const dockingBayGeodata = db.geodata ? JSON.parse(db.geodata) : null;
          if (dockingBayGeodata) {
            const dockingBayFeature = dockingBayGeodata.features[0];
            dockingBayFeature.properties = {
              ...feature.properties,
              height: db.height || 0,
              color: "#d0d5dd" /* "#443f3f"  */,
            };
            dockingBaysFeatures.push(dockingBayFeature);
          }
        });
      }

      return feature;
    });

  const parkingSlotsFeatures = [];

  // Prepare area features
  const areaFeatures = buildings
    .filter(
      (b) =>
        (b.buildingTypeId === dockingAreaTypeData?.id ||
          b.buildingTypeId === carParckingAreaTypeData?.id) &&
        b.geodata,
    )
    .map((item) => {
      const areaGeo = JSON.parse(item.geodata);
      const feature = areaGeo.features[0];
      const areaTypeData = buildingsTypes.find(
        (b) => b.id === item.buildingTypeId,
      );

      if (
        item.buildingTypeId === carParckingAreaTypeData?.id &&
        item.extra?.geodata
      ) {
        const parkingSlotsGeoData = JSON.parse(item.extra.geodata);
        const parkingSlotsFeature = parkingSlotsGeoData?.features[0];
        parkingSlotsFeatures.push(parkingSlotsFeature);
      }

      // Append building-specific props
      feature.properties = {
        ...feature.properties,
        color: areaTypeData?.color || "#6d6c6b",
        name: item.name.replace("dock_area_for_", "Docking Area for "),
        type: areaTypeData?.display_name || item.type,
        buildingId: item.buildingId,
        buildingTypeId: item.buildingTypeId,
      };

      // Calculate center for name label
      const centerPoint = turf.center(feature);
      buildingsCenter.push({
        ...centerPoint,
        properties: {
          ...centerPoint.properties,
          name: item.name.replace("dock_area_for_", "Docking Area for "),
        },
      });
      return feature;
    });

  useEffect(() => {
    if (map && mapLoaded && buildings) {
      // Add routes if given
      if (routes) {
        const truckRoutes = [];
        const carRoutes = [];
        const outlineRoutes = [];

        if (routes.length > 0 && routes[0].connectionType) {
          routes.forEach((r) => {
            const connectionTypeData = connectionTypes.find(
              (c) => c.id === r.connectionType,
            );
            if (connectionTypeData.edge_name === TRUCK_ROUTE_TYPE)
              truckRoutes.push(r);
            if (connectionTypeData.edge_name === CAR_ROUTE_TYPE)
              carRoutes.push(r);
            if (connectionTypeData.edge_name === OUTLINE_ROUTE_TYPE)
              outlineRoutes.push(r);
          });

          addRoutes(outlineRoutes, "outline-route");
          addRoutes(carRoutes, "car-route");
          addRoutes(truckRoutes, "truck-route");
        } else {
          addRoutes(routes, "outline-route");
        }
      }

      addOrUpdateBuildings(map);

      if (parkingSlotsFeatures?.length > 0) {
        addParkingSlots(parkingSlotsFeatures);
      }
      // Global click handler to unselect buildings when clicking outside:
      map.on("click", globalClickHandler);
    }

    // Cleanup on unmount
    return () => {
      if (isMapExist(map)) {
        map.off("click", globalClickHandler);
        removeBuildings();
      }
    };
  }, [map, mapLoaded, buildings]);

  const globalClickHandler = (e) => {
    if (map.getLayer("buildings-layer")) {
      const features = map.queryRenderedFeatures(e.point, {
        layers: ["buildings-layer", "areas-layer"],
      });

      if (
        !features.length ||
        features.find(
          (f) => f.properties?.buildingTypeId === dockingAreaTypeData?.id,
        )
      ) {
        // clicked outside polygons => clear highlight
        setSelectedBuilding(null);
        removeSelectedBuilding();
      }
    }
  };

  const addOrUpdateBuildings = (map) => {
    const buildingsGeoData = {
      type: "FeatureCollection",
      features: buildingFeatures,
    };

    const areaGeoData = {
      type: "FeatureCollection",
      features: areaFeatures,
    };

    const dockingBaysGeoData = {
      type: "FeatureCollection",
      features: dockingBaysFeatures,
    };

    // Add or update the buildings and area layer.
    addBuildingsAndAreasLayer(map, buildingsGeoData, areaGeoData);

    // Add or update the docking bays layer.
    addDockingBaysLayer(map, dockingBaysGeoData);

    // Listen for building clicks
    map.on("mousedown", "buildings-layer", onBuildingClick);
    // Listen for area clicks
    map.on("mousedown", "areas-layer", onBuildingClick);

    // Display building name labels
    addBuildingsName(buildingsCenter);
  };

  const addRoutes = (routeArray, id) => {
    const routeMiddleLine = [];
    const routeFeatures = routeArray.map((r) => {
      const geoData = JSON.parse(r.geodata);

      const feature = geoData?.features[0];
      const connectionTypeData = connectionTypes.find(
        (c) => c.id === r.connectionType,
      );

      // Append connection-specific props
      feature.properties = {
        ...feature.properties,
        color: connectionTypeData?.color || "#c7c7d6",
      };

      if (r.extra?.geodata) {
        const middleLineGeoData = JSON.parse(r.extra.geodata);
        const middleLineFeature = middleLineGeoData?.features[0];
        routeMiddleLine.push(middleLineFeature);
      }

      return feature;
    });

    const routesGeoData = {
      type: "FeatureCollection",
      features: routeFeatures,
    };

    const routesMiddleLineGeoData = {
      type: "FeatureCollection",
      features: routeMiddleLine,
    };

    addRoutesLayer(map, id, routesGeoData);
    addRoutesMiddleLineLayer(map, `${id}-middle-line`, routesMiddleLineGeoData);
  };

  const addParkingSlots = (parkingSlotsFeatures) => {
    const parkingSlotsGeoData = {
      type: "FeatureCollection",
      features: parkingSlotsFeatures,
    };

    addParkingSlotsLayer(map, parkingSlotsGeoData);
  };

  const onBuildingClick = (e) => {
    if (
      !e.features?.length ||
      e.features[0].properties.buildingTypeId === dockingAreaTypeData?.id /* ||
      e.features[0].properties.type === carParckingAreaTypeData?.id */
    ) {
      return;
    }

    const feature = e.features[0];
    const selectedBuildingData = buildings.find(
      (item) => item.buildingId === feature.properties.buildingId,
    );

    /*    map.setPaintProperty("buildings-layer", "fill-extrusion-opacity", [
      "case",
      ["==", ["get", "buildingId"], buildingId],
      1, // selected building
      0.5, // all others
    ]);
 */

    if (selectedBuildingData?.geodata) {
      const selectedBuildingGeoData = JSON.parse(selectedBuildingData.geodata);
      displaySelectedBuilding(
        selectedBuildingGeoData,
        feature.properties.height,
      );
    }

    setSelectedBuilding({
      ...feature.properties,
      ...selectedBuildingData,
      type: feature.properties.type,
    });

    // Set current building ID and axis grid representation
    let buildingId = selectedBuildingData?.buildingId;

    luckyorangeTrackEven("Results: select Building");

    setTimeout(() => {
      map.easeTo({ pitch: 0 });
    }, 100);

    /* // Stop event from also triggering the 'map.on("click")' outside logic
  e.originalEvent.stopPropagation();  */
  };

  const addBuildingsName = (buildingsNameFeatures) => {
    const buildingsNameGeoData = {
      type: "FeatureCollection",
      features: buildingsNameFeatures,
    };

    addBuildingsNameLabels(map, buildingsNameGeoData);
  };

  const displaySelectedBuilding = (buildingGeoData, height) => {
    const buildingFeature = buildingGeoData.features[0];
    addEdgesLayer(buildingFeature, height);
    displayVertexAndDistanceLabels(buildingFeature);
    /* addPolygonVerticesAsCircles(map, buildingGeoData.features[0], "points-building", "points-building-layer") */
  };

  const addEdgesLayer = (propertyFeature, height) => {
    if (!propertyFeature) return;
    const lineFeatures = polygonToLines(propertyFeature);
    const buildingEdgesGeoData = {
      type: "FeatureCollection",
      features: lineFeatures,
    };

    // console.log('Adding building edges layer:', buildingEdgesGeoData);
    removeBuildingEdgesLayer(map);
    addBuildingEdgesLayer(map, buildingEdgesGeoData, height);
  };

  const addAxisGridLayer = (axisGridShape, height) => {
    if (!axisGridShape || !showGrid) return;

    removeBuildingAxisGridLayer(map);
    addBuildingAxisGridLayer(map, axisGridShape, height);
  };

  const displayVertexAndDistanceLabels = (polygon) => {
    const { distanceFeatures, vertexFeatures } =
      createDistanceAndVertexFeatures(polygon);
    addDistanceLabels(map, distanceFeatures);
    /* addVertexLabels(map, vertexFeatures); */
  };

  function addDistanceLabels(map, distanceFeatures) {
    const distanceGeoData = {
      type: "FeatureCollection",
      features: distanceFeatures,
    };
    addBuildingDistanceLabels(map, distanceGeoData);
  }

  function addVertexLabels(map, vertexFeatures) {
    const sourceId = "vertex-labels-building";

    map.loadImage("img-path", (error, image) => {
      if (error) throw error;
      if (!map.hasImage("test-icon")) {
        map.addImage("test-icon", image);
      }
    });

    addOrUpdateSource(map, sourceId, {
      type: "FeatureCollection",
      features: vertexFeatures,
    });
    addLayerIfNotExists(map, {
      id: sourceId,
      type: "symbol",
      source: sourceId,
      layout: {
        /* "symbol-placement": "point",
        "symbol-z-order": "auto",
        "symbol-z-elevate": true, */

        /*         // 1. Icon properties
    'icon-image': 'test-icon',        // name of the sprite image or added image
    'icon-size': 0.5,               // scale the icon
    'icon-allow-overlap': true,     // allow icon to overlap other symbols */

        // 2. Text properties
        "text-field": ["get", "label"],
        "text-size": 14,
        "text-offset": [0, -1],
        "text-anchor": "bottom",
        "text-allow-overlap": true,
      },
      paint: {
        "text-color": "#343745",
        "text-halo-color": "#ffffff",
        "text-halo-width": 1,
        "text-background-color": "#ffffff",
        "text-background-padding": [2, 2],
      },
    });
  }

  const removeSelectedBuilding = () => {
    removeBuildingEdgesLayer(map);
    removeBuildingAxisGridLayer(map);
    removeBuildingDistanceLabels(map);
  };

  const removeBuildings = () => {
    if (!isMapExist(map)) return;

    if (map.getLayer("buildings-layer")) {
      map.off("mousedown", "buildings-layer", onBuildingClick);
    }
    removeBuildingsAndAreasLayer(map);
    removeBuildingsNameLabels(map);
    removeSelectedBuilding();
    removeDockingBaysLayer(map);
    removeParkingSlotsLayer(map);

    removeRoutesLayer(map, "outline-route");
    removeRoutesLayer(map, "car-route");
    removeRoutesLayer(map, "truck-route");
    removeRoutesMiddleLineLayer(map, "outline-route-middle-line");
    removeRoutesMiddleLineLayer(map, "car-route-middle-line");
    removeRoutesMiddleLineLayer(map, "truck-route-middle-line");
  };

  return null;
};
